import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { queryClient } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth-store';
import { useBrandStore } from '@/stores/brand-store';

const DEFAULT_TENANT_SLUG = import.meta.env.VITE_DEFAULT_TENANT ?? '';

/**
 * Resolve the tenant the same way the backend does.
 *
 * In production each organisation has its own host, so the subdomain identifies it and
 * no header is needed. In development everything runs on localhost, so the slug comes
 * from a query parameter (once, then persisted) or from an environment default.
 */
function resolveTenantSlug(): string | null {
  const fromQuery = new URLSearchParams(window.location.search).get('tenant');
  if (fromQuery) return fromQuery.trim().toLowerCase();

  const { hostname } = window.location;
  const parts = hostname.split('.');
  if (parts.length > 2 && parts[0] && parts[0] !== 'www') return parts[0];

  return DEFAULT_TENANT_SLUG || null;
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  const setTenantSlug = useAuthStore((state) => state.setTenantSlug);
  const restore = useAuthStore((state) => state.restore);
  const loadBrand = useBrandStore((state) => state.load);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const slug = resolveTenantSlug() ?? useAuthStore.getState().tenantSlug;
      if (slug) setTenantSlug(slug);

      // Branding is fetched before the shell renders so the interface never flashes the
      // default palette before switching to the tenant's.
      await Promise.all([loadBrand(), restore()]);

      if (!cancelled) setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [loadBrand, restore, setTenantSlug]);

  if (!ready) {
    /*
     * The first paint, before branding or the session are known.
     *
     * Deliberately not the shared LoadingState: this renders against the default
     * palette, so it stays a neutral mark that cannot clash with the tenant's colours
     * a moment later. The pulse is pure CSS — no component tree worth mounting for
     * something that lives for a few hundred milliseconds.
     */
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background" role="status" aria-live="polite">
        <span className="relative flex size-10 items-center justify-center">
          <span className="absolute inset-0 animate-pulse-ring rounded-full bg-primary/25" />
          <span className="size-2.5 rounded-full bg-primary" />
        </span>
        <span className="sr-only">Starting</span>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      {/* `delayDuration` is deliberately short: these tooltips label icon-only
          controls, and a long delay makes an unlabelled button feel unlabelled. */}
      <TooltipProvider delayDuration={250} skipDelayDuration={400}>
        {children}
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
